import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type { Book, CreateBook, UpdateBook } from '@/types'

const BOOKS_QUERY_KEY = 'books'

// Dashboard aggregates are cached separately; refresh them whenever the library changes
const DASHBOARD_QUERY_KEYS = [
  'dashboard-statistics',
  'yearly-progress',
  'library-status-by-type',
  'rating-distribution',
  'top-genres',
  'favorite-genre',
]

export function invalidateLibraryQueries(queryClient: QueryClient, userId?: string) {
  queryClient.invalidateQueries({ queryKey: [BOOKS_QUERY_KEY, userId] })
  for (const key of DASHBOARD_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey: [key, userId] })
  }
}

// Get all books for the current user
export function useBooks() {
  const { user } = useAuth()

  return useQuery({
    queryKey: [BOOKS_QUERY_KEY, user?.id],
    queryFn: async (): Promise<Book[]> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('books')
        .select(`
          *,
          book_genres (
            genre:genres (
              id,
              name,
              color
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        captureError(error, { context: 'useBooks.queryFn', userId: user.id })
        throw error
      }
      return data || []
    },
    enabled: !!user?.id,
  })
}

// Get a single book by ID
export function useBook(id: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [BOOKS_QUERY_KEY, id],
    queryFn: async (): Promise<Book> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('books')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id) // Ensure user can only access their own books
        .single()

      if (error) {
        captureError(error, { context: 'useBook.queryFn', bookId: id, userId: user.id })
        throw error
      }
      return data
    },
    enabled: !!id && !!user?.id,
  })
}

// Check if ISBN already exists for the user
export function useCheckISBNExists() {
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (
      isbn: string
    ): Promise<{ exists: boolean; existingBook?: { id: string; title: string } }> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')
      if (!isbn?.trim()) return { exists: false }

      const { data, error } = await supabase
        .from('books')
        .select('id, title')
        .eq('user_id', user.id)
        .eq('isbn', isbn.trim())
        .limit(1)

      if (error) {
        captureError(error, { context: 'useCheckISBNExists', isbn, userId: user.id })
        throw error
      }

      return {
        exists: data && data.length > 0,
        existingBook: data?.[0],
      }
    },
  })
}

// Create a new book
export function useCreateBook() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (book: CreateBook): Promise<Book> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const bookWithUser = {
        ...book,
        user_id: user.id,
      }

      const { data, error } = await supabase.from('books').insert(bookWithUser).select().single()

      if (error) {
        captureError(error, { context: 'useCreateBook', userId: user.id })
        throw error
      }
      return data
    },
    onSuccess: () => {
      invalidateLibraryQueries(queryClient, user?.id)
    },
  })
}

// Update an existing book
export function useUpdateBook() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async ({
      bookId,
      data: updates,
    }: {
      bookId: string
      data: Partial<UpdateBook>
    }): Promise<Book> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('books')
        .update(updates)
        .eq('id', bookId)
        .eq('user_id', user.id) // Ensure user can only update their own books
        .select()
        .single()

      if (error) {
        captureError(error, { context: 'useUpdateBook', bookId, userId: user.id })
        throw error
      }
      return data
    },
    onSuccess: (data) => {
      invalidateLibraryQueries(queryClient, user?.id)
      queryClient.setQueryData([BOOKS_QUERY_KEY, data.id], data)
    },
  })
}

// Delete a book
export function useDeleteBook() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { error } = await supabase.from('books').delete().eq('id', id).eq('user_id', user.id) // Ensure user can only delete their own books

      if (error) {
        captureError(error, { context: 'useDeleteBook', bookId: id, userId: user.id })
        throw error
      }
    },
    onSuccess: () => {
      invalidateLibraryQueries(queryClient, user?.id)
    },
  })
}
